import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { SeoFaq } from "@/components/seo/SeoFaq";
import { SeoLinkList } from "@/components/seo/SeoLinkList";
import {
 breadcrumbListSchema,
 collectionPageSchema,
 faqPageSchema,
 jsonLdScript,
} from "@/lib/seo-schema";
import { decideIndexability, robotsMeta } from "@/lib/seo-indexability";
import { specialtySlug, specialtyFromSlug, SEO_CITIES, SEO_INSURANCE_IDS } from "@/lib/seo-slugs";
import { INSURANCES, SPECIALTIES } from "@/data/heallexa";
import { setSeoPageCache } from "@/lib/page-cache";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/specialties/$slug")({
 loader: async ({ params }) => {
 await setSeoPageCache();
 const slug = params.slug.toLowerCase();
 // Legacy letter index, single character like /specialties/a
 if (/^[a-z]$/.test(slug)) {
 const letter = slug.toUpperCase();
 const items = SPECIALTIES.filter((s) => s[0].toUpperCase() === letter);
 if (items.length === 0) throw notFound();
 return { kind: "letter" as const, letter, items };
 }
 const specialty = specialtyFromSlug(slug);
 if (!specialty) throw notFound();
 return { kind: "specialty" as const, specialty };
 },
 head: ({ params, loaderData }) => {
 if (!loaderData) return {};
 if (loaderData.kind === "letter") {
 const title = `Specialties Starting with "${loaderData.letter}" | Heallexa`;
 const description = `Browse healthcare specialties that start with the letter ${loaderData.letter}.`;
 const url = `${SITE}/specialties/${loaderData.letter.toLowerCase()}`;
 return {
 meta: [
 { title },
 { name: "description", content: description },
 { property: "og:title", content: title },
 { property: "og:description", content: description },
 { property: "og:url", content: url },
 ],
 links: [{ rel: "canonical", href: url }],
 };
 }
 const spec = loaderData.specialty;
 const title = `${spec}, Specialty Overview & Top Cities | Heallexa`;
 const description = `Learn what ${spec.toLowerCase()} do, common services, related specialties, and find ${spec.toLowerCase()} in top U.S. cities on Heallexa.`;
 const url = `${SITE}/specialties/${params.slug}`;
 const decision = decideIndexability({
 providerCount: 1,
 hasFaq: true,
 hasNearbyCities: true,
 hasRelatedProviders: true,
 internalLinkCount: 30,
 });
 return {
 meta: [
 { title },
 { name: "description", content: description },
 robotsMeta(decision.robots),
 { property: "og:title", content: title },
 { property: "og:description", content: description },
 { property: "og:url", content: url },
 { property: "og:type", content: "website" },
 ],
 links: [{ rel: "canonical", href: url }],
 scripts: [
 jsonLdScript(collectionPageSchema({ name: title, description, url })),
 jsonLdScript(
 breadcrumbListSchema([
 { name: "Home", url: "/" },
 { name: "Specialties", url: "/specialties" },
 { name: spec, url: `/specialties/${params.slug}` },
 ]),
 ),
 jsonLdScript(
 faqPageSchema([
 { q: `What does a ${spec.toLowerCase().replace(/s$/, "")} do?`, a: `A ${spec.toLowerCase().replace(/s$/, "")} provides specialized healthcare services. Confirm scope of practice with the provider.` },
 { q: `How do I find a ${spec.toLowerCase().replace(/s$/, "")} near me?`, a: `Use Heallexa to filter ${spec.toLowerCase()} by city and insurance.` },
 { q: `Do ${spec.toLowerCase()} take my insurance?`, a: `Many ${spec.toLowerCase()} accept major plans like BCBS, Aetna, UnitedHealthcare, Cigna, and Medicare. Always verify with the office.` },
 ]),
 ),
 ],
 };
 },
 component: SpecialtySlugPage,
});

function SpecialtySlugPage() {
 const data = Route.useLoaderData();
 if (data.kind === "letter") return <LetterView letter={data.letter} items={data.items} />;
 return <SpecialtyHubView specialty={data.specialty} />;
}

function LetterView({ letter, items }: { letter: string; items: string[] }) {
 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-5xl px-6 py-16">
 <Breadcrumbs
 className="mb-6"
 items={[
 { name: "Sitemap", href: "/sitemap" },
 { name: "Specialties", href: "/specialties" },
 { name: letter },
 ]}
 />
 <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-foreground mb-3">
 Specialties, {letter}
 </h1>
 <p className="text-muted-foreground mb-10">
 {items.length} specialt{items.length === 1 ? "y" : "ies"} starting with {letter}.
 </p>
 <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-3 gap-x-6">
 {items.map((s) => (
 <li key={s}>
 <Link
 to="/specialties/$slug"
 params={{ slug: specialtySlug(s) }}
 className="text-sm text-foreground hover:text-primary transition"
 >
 {s}
 </Link>
 </li>
 ))}
 </ul>
 </main>
 <FooterSection />
 </div>
 );
}

function SpecialtyHubView({ specialty }: { specialty: string }) {
 const lower = specialty.toLowerCase();
 const singular = specialty.replace(/s$/, "");

 const cityLinks = SEO_CITIES.slice(0, 24).map((c) => {
 const [city, st] = c.split(",").map((x) => x.trim());
 return {
 label: `${specialty} in ${city}, ${st}`,
 href: `/specialty/${specialtySlug(specialty)}/${(`${city}-${st}`).toLowerCase().replace(/\s+/g, "-")}`,
 };
 });

 const insuranceLinks = SEO_INSURANCE_IDS.map((id) => INSURANCES.find((i) => i.id === id)).filter(Boolean).map((ins) => ({
 label: `${specialty} accepting ${ins!.short}`,
 href: `/insurance/${ins!.id}`,
 }));

 const relatedSpecialties = SPECIALTIES.filter((s) => s !== specialty).slice(0, 12).map((s) => ({
 label: s,
 href: `/specialties/${specialtySlug(s)}`,
 }));

 const faq = [
 { q: `What does a ${singular.toLowerCase()} do?`, a: `${specialty} are healthcare professionals who specialize in their field. They diagnose, treat, and help manage conditions related to their specialty.` },
 { q: `When should I see a ${singular.toLowerCase()}?`, a: `Most people see a ${singular.toLowerCase()} when referred by a primary care doctor or for ongoing specialty care. You can also self-refer in most states.` },
 { q: `Do ${lower} accept insurance?`, a: `Most ${lower} on Heallexa accept major insurance carriers. Filter by insurance to find in-network providers.` },
 { q: `How do I prepare for my first visit?`, a: `Bring your insurance card, a photo ID, a list of medications, and any relevant records or imaging from prior visits.` },
 ];

 return (
 <div className="min-h-screen bg-background">
 <Navbar />
 <main className="mx-auto max-w-5xl px-6 py-12">
 <Breadcrumbs
 className="mb-6"
 items={[
 { name: "Home", href: "/" },
 { name: "Specialties", href: "/specialties" },
 { name: specialty },
 ]}
 />

 <header className="mb-10">
 <p className="text-sm font-medium text-primary mb-2">Specialty overview</p>
 <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-foreground">
 {specialty}
 </h1>
 <p className="mt-4 text-muted-foreground max-w-2xl">
 Find and compare {lower} across the United States. Browse by city, filter by insurance, and read patient reviews, free on Heallexa.
 </p>
 <div className="mt-6 flex flex-wrap gap-3">
 <Link
 to="/search"
 search={{ specialty }}
 className="inline-block px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition"
 >
 Search all {lower}
 </Link>
 </div>
 </header>

 <section className="mb-10 prose prose-neutral max-w-none">
 <h2 className="text-2xl font-semibold mb-3">About {lower}</h2>
 <p className="text-muted-foreground">
 {specialty} provide specialized care across hospitals, group practices, and independent clinics. When choosing one, patients typically weigh insurance acceptance, location, and patient reviews.
 </p>
 </section>

 <SeoLinkList heading={`Find ${lower} by city`} links={cityLinks} columns={3} className="mb-8" />
 <SeoLinkList heading={`Find ${lower} by insurance`} links={insuranceLinks} columns={3} className="mb-8" />
 <SeoLinkList heading="Related specialties" links={relatedSpecialties} columns={3} className="mb-8" />

 <SeoFaq items={faq} className="mb-8" />
 </main>
 <FooterSection />
 </div>
 );
}
